import React, { useEffect, useState, useRef } from 'react';

const WelcomePage = () => {
  const [gatesOpen, setGatesOpen] = useState(false);
  const [lightVisible, setLightVisible] = useState(false);
  const [messageVisible, setMessageVisible] = useState(false);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);

  // Play champagne pop and applause sound
  const playChampagneSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume audio context (needed for mobile)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      // LOUD champagne cork POP
      const createCorkPop = (time, pan = 0) => {
        // Initial explosive pop
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const panner = audioContext.createStereoPanner();
        
        osc.type = 'square';
        osc.frequency.setValueAtTime(150, time);
        osc.frequency.exponentialRampToValueAtTime(40, time + 0.1);
        
        gain.gain.setValueAtTime(1, time);
        gain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);
        
        panner.pan.value = pan;
        
        osc.connect(gain);
        gain.connect(panner);
        panner.connect(audioContext.destination);
        
        osc.start(time);
        osc.stop(time + 0.15);

        // High frequency burst for "pop" effect
        const noise = audioContext.createBufferSource();
        const buffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.1, audioContext.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (buffer.length * 0.05));
        }
        
        noise.buffer = buffer;
        
        const noiseGain = audioContext.createGain();
        noiseGain.gain.setValueAtTime(0.8, time);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);
        
        const noisePanner = audioContext.createStereoPanner();
        noisePanner.pan.value = pan;
        
        noise.connect(noiseGain);
        noiseGain.connect(noisePanner);
        noisePanner.connect(audioContext.destination);
        
        noise.start(time);
      };

      // Fizzing bubbles sound
      const createFizz = (time) => {
        const noise = audioContext.createBufferSource();
        const buffer = audioContext.createBuffer(1, audioContext.sampleRate * 2, audioContext.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (buffer.length * 0.4));
        }
        
        noise.buffer = buffer;
        
        const filter = audioContext.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 4000;
        
        const gain = audioContext.createGain();
        gain.gain.setValueAtTime(0.2, time);
        gain.gain.exponentialRampToValueAtTime(0.01, time + 2);
        
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(audioContext.destination);
        
        noise.start(time);
      };

      // Applause/celebration
      const createApplause = () => {
        for (let i = 0; i < 25; i++) {
          const noise = audioContext.createBufferSource();
          const buffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.06, audioContext.sampleRate);
          const data = buffer.getChannelData(0);
          
          for (let j = 0; j < buffer.length; j++) {
            data[j] = (Math.random() * 2 - 1) * Math.exp(-j / (buffer.length * 0.12));
          }
          
          noise.buffer = buffer;
          
          const filter = audioContext.createBiquadFilter();
          filter.type = 'bandpass';
          filter.frequency.value = 700 + Math.random() * 500;
          
          const gain = audioContext.createGain();
          gain.gain.value = 0.12;
          
          const panner = audioContext.createStereoPanner();
          panner.pan.value = (Math.random() - 0.5) * 2;
          
          noise.connect(filter);
          filter.connect(gain);
          gain.connect(panner);
          panner.connect(audioContext.destination);
          
          noise.start(audioContext.currentTime + 0.3 + i * 0.05 + Math.random() * 0.03);
        }
      };

      // Play the sounds - TWO champagne bottles popping!
      createCorkPop(audioContext.currentTime, -0.8);        // Left bottle
      createCorkPop(audioContext.currentTime + 0.12, 0.8);  // Right bottle
      createFizz(audioContext.currentTime + 0.1);           // Fizzing
      createApplause();                                      // Applause
      
    } catch (e) {
      console.log('Audio error:', e);
    }
  };

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setGatesOpen(true);
      playChampagneSound();
    }, 600);

    const timer2 = setTimeout(() => {
      setLightVisible(true);
      setMessageVisible(true);
    }, 1000);

    const timer3 = setTimeout(() => {
      spawnConfetti();
      animateConfetti();
    }, 800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const spawnConfetti = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const goldColors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
      '#BB8FCE', '#85C1E9', '#F8B500', '#FF8C00',
      '#1A4D2E', '#E74C3C', '#3498DB', '#2ECC71'
    ];

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    // Adjust particle count based on screen size
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 300 : 500;

    // Center burst
    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 18 + 8;
      
      particlesRef.current.push({
        x: centerX + (Math.random() - 0.5) * 50,
        y: centerY + (Math.random() - 0.5) * 30,
        vx: Math.cos(angle) * speed * (0.5 + Math.random()),
        vy: Math.sin(angle) * speed * (0.5 + Math.random()) - Math.random() * 6,
        size: Math.random() * (isMobile ? 8 : 10) + 3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 18,
        life: Math.random() * 280 + 180,
        color: goldColors[Math.floor(Math.random() * goldColors.length)],
        shape: Math.random() > 0.4 ? 'rect' : 'circle',
      });
    }

    // Second wave
    setTimeout(() => {
      for (let i = 0; i < particleCount * 0.6; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 15 + 6;
        
        particlesRef.current.push({
          x: centerX + (Math.random() - 0.5) * 80,
          y: centerY + (Math.random() - 0.5) * 40,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - Math.random() * 4,
          size: Math.random() * (isMobile ? 6 : 8) + 2,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 15,
          life: Math.random() * 220 + 140,
          color: goldColors[Math.floor(Math.random() * goldColors.length)],
          shape: Math.random() > 0.5 ? 'rect' : 'circle',
        });
      }
    }, 200);
  };

  const animateConfetti = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current = particlesRef.current.filter(p => p.life > 0);

    particlesRef.current.forEach((p) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      
      const alpha = Math.min(1, p.life / 50);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      
      if (p.shape === 'rect') {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.restore();

      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.18;
      p.vx *= 0.99;
      p.rotation += p.rotationSpeed;
      p.life--;
    });

    animationRef.current = requestAnimationFrame(animateConfetti);
  };

  return (
    <div
      className="welcome-container"
      data-testid="welcome-page"
    >
      {/* Left Gate */}
      <div
        className="gate gate-left"
        style={{
          transform: gatesOpen ? 'rotateY(-105deg)' : 'rotateY(0deg)',
        }}
      >
        <div className="gate-handle gate-handle-right" />
      </div>

      {/* Right Gate */}
      <div
        className="gate gate-right"
        style={{
          transform: gatesOpen ? 'rotateY(105deg)' : 'rotateY(0deg)',
        }}
      >
        <div className="gate-handle gate-handle-left" />
      </div>

      {/* Golden Light */}
      <div
        className="golden-light"
        style={{
          opacity: lightVisible ? 1 : 0,
          transform: lightVisible ? 'scale(1.2)' : 'scale(0.8)',
        }}
      />

      {/* Confetti Canvas */}
      <canvas
        ref={canvasRef}
        className="confetti-canvas"
      />

      {/* Message */}
      <div
        className="message-container"
        style={{
          opacity: messageVisible ? 1 : 0,
          transform: messageVisible ? 'scale(1)' : 'scale(0.8)',
        }}
      >
        <h1 className="welcome-title" data-testid="welcome-title">
          SWIPE. MATCH. START.
        </h1>
        <p className="welcome-tagline">In één app.</p>

        <p className="welcome-subtitle" data-testid="welcome-subtitle">
          WELCOME TO THE TALENTFIXR.
          <br />
          COMMUNITY
        </p>
      </div>

      <style>{`
        .welcome-container {
          position: relative;
          width: 100%;
          height: 100vh;
          background: #F5EFE6;
          overflow: hidden;
          color: #1A4D2E;
          font-family: Inter, system-ui, sans-serif;
          perspective: 1200px;
        }

        .gate {
          position: absolute;
          top: 0;
          width: 50%;
          height: 100%;
          background: linear-gradient(135deg, #1A4D2E 0%, #0d2818 50%, #0a1f14 100%);
          z-index: 4;
          transition: transform 0.7s cubic-bezier(0.2, 0.8, 0.2, 1);
        }

        .gate-left {
          left: 0;
          transform-origin: left center;
          box-shadow: 20px 0 60px rgba(0,0,0,0.8);
        }

        .gate-right {
          right: 0;
          transform-origin: right center;
          background: linear-gradient(-135deg, #1A4D2E 0%, #0d2818 50%, #0a1f14 100%);
          box-shadow: -20px 0 60px rgba(0,0,0,0.8);
        }

        .gate-handle {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 12px;
          height: 50px;
          background: linear-gradient(180deg, #D4AF37, #B8860B);
          border-radius: 6px;
        }

        .gate-handle-right {
          right: 30px;
        }

        .gate-handle-left {
          left: 30px;
        }

        .golden-light {
          position: absolute;
          inset: -20%;
          background: radial-gradient(circle at center, rgba(26, 77, 46, 0.1) 0%, rgba(245, 239, 230, 0.3) 30%, transparent 60%);
          transition: opacity 0.6s ease, transform 0.8s ease;
          z-index: 1;
        }

        .confetti-canvas {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 5;
        }

        .message-container {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          transition: opacity 0.5s ease, transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
          z-index: 2;
          padding: 20px;
        }

        .welcome-title {
          font-size: 36px;
          margin-bottom: 8px;
          font-weight: 700;
          line-height: 1.1;
          color: #1A4D2E;
          letter-spacing: 2px;
        }

        .welcome-tagline {
          font-size: 18px;
          font-style: italic;
          color: #1A4D2E;
          opacity: 0.8;
          margin-bottom: 30px;
        }

        .welcome-subtitle {
          color: #1A4D2E;
          font-size: 20px;
          line-height: 1.4;
          font-weight: 600;
          letter-spacing: 1px;
        }

        /* Tablet and up */
        @media (min-width: 768px) {
          .gate-handle {
            width: 14px;
            height: 55px;
          }

          .gate-handle-right {
            right: 35px;
          }

          .gate-handle-left {
            left: 35px;
          }

          .welcome-title {
            font-size: 56px;
            margin-bottom: 12px;
          }

          .welcome-tagline {
            font-size: 22px;
            margin-bottom: 40px;
          }

          .welcome-subtitle {
            font-size: 28px;
          }
        }

        /* Desktop */
        @media (min-width: 1024px) {
          .gate-handle {
            width: 16px;
            height: 60px;
          }

          .gate-handle-right {
            right: 40px;
          }

          .gate-handle-left {
            left: 40px;
          }

          .welcome-title {
            font-size: 72px;
            margin-bottom: 16px;
            letter-spacing: 4px;
          }

          .welcome-tagline {
            font-size: 26px;
            margin-bottom: 50px;
          }

          .welcome-subtitle {
            font-size: 36px;
          }
        }
      `}</style>
    </div>
  );
};

export default WelcomePage;
